import React, { useState } from "react";
import {
  clearKitchenPrinter,
  getKitchenPrinterName,
  isBluetoothSupported,
  pairKitchenPrinter,
} from "../pages/pos/bluetoothPrinter";

interface Props {
  onClose: () => void;
}

const KitchenPrinterModal: React.FC<Props> = ({ onClose }) => {
  const [printerName, setPrinterName] = useState<string | null>(
    getKitchenPrinterName,
  );
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePair = async () => {
    setPairing(true);
    setError(null);
    try {
      const name = await pairKitchenPrinter();
      setPrinterName(name);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Pairing failed");
    } finally {
      setPairing(false);
    }
  };

  const handleClear = () => {
    clearKitchenPrinter();
    setPrinterName(null);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold m-0">Kitchen Printer</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {!isBluetoothSupported() && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-800">
            Web Bluetooth is not supported on this browser. Use Chrome on
            Android.
          </div>
        )}

        {/* Paired printer status */}
        <div className="border border-gray-200 rounded-xl p-4 mb-5">
          <p className="text-sm font-medium mb-2">Paired kitchen printer</p>
          {printerName ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">🖨 {printerName}</span>
              <button
                onClick={handleClear}
                className="text-xs text-red-500 hover:text-red-700 underline"
              >
                Remove
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-400">No kitchen printer paired yet.</p>
          )}

          <button
            onClick={handlePair}
            disabled={pairing || !isBluetoothSupported()}
            className="mt-3 w-full py-2 rounded-lg bg-black text-white text-sm font-medium disabled:opacity-50"
          >
            {pairing
              ? "Searching…"
              : printerName
              ? "Re-pair Printer"
              : "Pair Kitchen Printer"}
          </button>

          {error && <p className="text-xs text-red-500 mt-2">{error}</p>}

          <p className="text-xs text-gray-400 mt-3">
            Tap the button to open the Bluetooth device picker and select your
            kitchen printer. After pairing once, kitchen tickets will print
            automatically — one ticket per item, cut after each.
          </p>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl bg-black text-white font-medium text-sm"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default KitchenPrinterModal;
